import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../api/api';
import './order-complaint.css';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Video from "yet-another-react-lightbox/plugins/video";
import { FaPlus, FaImage, FaVideo, FaTimes } from "react-icons/fa";
import Loader from '../loader/Loader';

const MAX_IMAGE_COUNT = 5;
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
const MAX_VIDEO_SIZE = 20 * 1024 * 1024; // 20MB in bytes
const MAX_VIDEO_COUNT = 5; // Updated to match image count

const OrderComplaintModal = ({ showModal, setShowModal, orderId }) => {
    const { t } = useTranslation();
    const token = useSelector(state => state.user?.jwtToken);
    const [complainDescription, setComplainDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [complaints, setComplaints] = useState([]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [currentComplaintMedia, setCurrentComplaintMedia] = useState([]);
    const [showComplaintForm, setShowComplaintForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [videos, setVideos] = useState([]);
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [videoPreviews, setVideoPreviews] = useState([]);
    const [isUserBlocked, setIsUserBlocked] = useState(false);

    const handleClose = () => {
        setShowModal(false);
        resetForm();
    };

    const handleFetchComplaints = async () => {
        try {
            setIsLoading(true);
            const response = await api.getOrderComplaints(token, orderId);
            const result = await response.json();
            if (result?.data?.length > 0) {
                setComplaints(result?.data);
                setIsUserBlocked(result?.data?.[0]?.is_user_block == 1)
            }
        } catch (error) {
            console.error('Error fetching complaints:', error);
            toast.error(t('error_fetching_complaints'));
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (showModal) {
            handleFetchComplaints();
        }
    }, [showModal]);

    const resetForm = () => {
        setComplainDescription('');
        setVideos([]);
        setImages([]);
        setImagePreviews([]);
        setVideoPreviews([]);
    };

    const validateImageFile = (file) => {
        if (images.length >= MAX_IMAGE_COUNT) {
            toast.error(t('max_image_count_error'));
            return false;
        }

        if (file.size > MAX_IMAGE_SIZE) {
            toast.error(t('image_size_error'));
            return false;
        }

        if (!file.type.match('image.*')) {
            toast.error(t('invalid_image_type'));
            return false;
        }

        return true;
    };

    const handleImageChange = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const newImages = [...images];
        const remainingSlots = MAX_IMAGE_COUNT - newImages.length;

        // Limit to remaining slots available
        const filesToProcess = Math.min(files.length, remainingSlots);

        // Process each file sequentially to avoid issues with FileReader
        Array.from(files).slice(0, filesToProcess).forEach(file => {
            if (validateImageFile(file)) {
                newImages.push(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    // Use functional state update to ensure we're working with latest state
                    setImagePreviews(prevPreviews => [...prevPreviews, reader.result]);
                };
                reader.readAsDataURL(file);
            }
        });

        setImages(newImages);
    };

    const validateVideoFile = (file) => {
        if (videos.length >= MAX_VIDEO_COUNT) {
            toast.error(t('max_video_count_error'));
            return false;
        }

        if (file.size > MAX_VIDEO_SIZE) {
            toast.error(t('video_size_error'));
            return false;
        }
        const validVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/wmv'];
        if (!validVideoTypes.some(type => file.type.includes(type))) {
            toast.error(t('invalid_video_type'));
            return false;
        }

        return true;
    };

    const handleVideoChange = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const newVideos = [...videos];
        const remainingSlots = MAX_VIDEO_COUNT - newVideos.length;

        // Limit to remaining slots available
        const filesToProcess = Math.min(files.length, remainingSlots);

        // Process files up to the limit
        for (let i = 0; i < filesToProcess; i++) {
            const file = files[i];
            if (validateVideoFile(file)) {
                newVideos.push(file);
                // Use functional state update to ensure we're working with latest state
                setVideoPreviews(prevPreviews => [
                    ...prevPreviews,
                    URL.createObjectURL(file)
                ]);
            }
        }

        setVideos(newVideos);
    };

    const handleRemoveImage = (index) => {
        const newImages = [...images];
        newImages.splice(index, 1);
        setImages(newImages);

        const newPreviews = [...imagePreviews];
        newPreviews.splice(index, 1);
        setImagePreviews(newPreviews);
    };

    const handleRemoveVideo = (index) => {
        const newVideos = [...videos];
        newVideos.splice(index, 1);
        setVideos(newVideos);

        const newPreviews = [...videoPreviews];
        // Revoke the URL to prevent memory leaks
        URL.revokeObjectURL(newPreviews[index]);
        newPreviews.splice(index, 1);
        setVideoPreviews(newPreviews);
    };

    const openLightbox = (complaint, startIndex = 0) => {
        // Create slides for lightbox
        const slides = [
            ...(complaint.images || []).map(img => ({
                src: img,
            })),
            ...(complaint.videos || []).map(video => ({
                type: "video",
                sources: [
                    {
                        src: video,
                        type: "video/mp4",
                    }
                ]
            }))
        ];

        setCurrentComplaintMedia(slides);
        setLightboxIndex(startIndex);
        setLightboxOpen(true);
    };

    const handleSubmit = async () => {
        if (!complainDescription.trim()) {
            toast.error(t('please_enter_complaint_title'));
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await api.postOrderComplaint(
                token,
                orderId,
                complainDescription,
                images,
                videos
            );
            const result = await response.json();
            console.log(result)
            if (result?.status == 1) {
                toast.success(t('complaint_submitted_successfully'));
                handleClose();
                handleFetchComplaints();
                setShowComplaintForm(false);
            }
        } catch (error) {
            console.error('Error submitting complaint:', error);
            toast.error(t('something_went_wrong'));
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={showModal}
            onHide={handleClose}
            keyboard={true}
            centered
            className='order-complaint-modal'
        >
            <Modal.Header className='complaint-modal-header'>
                <Modal.Title>
                    {t('order_complaints')}
                    <button onClick={handleClose} className='close-btn'>
                        <AiOutlineCloseCircle size={24} />
                    </button>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className='complaint-modal-body'>
                {!showComplaintForm ? (
                    <div className="complaint-list overflow-y-auto">
                        {isLoading ? (
                            <Loader width="100%" height="200px" />
                        ) : complaints.length > 0 ? (
                            complaints.map((complaint) => (
                                <div key={complaint.id} className="complaint-item">
                                    <div className="complaint-item-header">
                                        <div className="header d-flex flex-column justify-content-start">
                                            <h3>{t('id')} #{complaint.id}</h3>
                                            <p>{new Date(complaint.created_at).toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                            })}</p>
                                        </div>
                                        {complaint?.status === 'pending' && (
                                            <div className="complaint-item-status">
                                                <p className='pending'>{t('pending')}</p>
                                            </div>
                                        )}
                                        {complaint?.status === "under_review" && (
                                            <div className="complaint-item-status">
                                                <p className='under_review'>{t('under_review')}</p>
                                            </div>
                                        )}
                                        {complaint?.status === "resolved" && (
                                            <div className="complaint-item-status">
                                                <p className='resolved'>{t('resolved')}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className='complaint-item-body'>
                                        <p>{complaint?.description}</p>
                                        {complaint?.images?.length > 0 && (
                                            <div className="complaint-item-images">
                                                {complaint?.images?.slice(0, complaint?.images?.length > 3 || complaint?.videos?.length > 0 ? 3 : 4).map((image, index, array) => (
                                                    <div
                                                        key={index}
                                                        className="image-wrapper"
                                                    >
                                                        <img
                                                            src={image}
                                                            className='complaint-image'
                                                            alt="Complaint"
                                                            onClick={() => openLightbox(complaint, index)}
                                                        />
                                                        {/* Show overlay on the last visible image only if there are more images or videos */}
                                                        {(index === array.length - 1 && (complaint?.images?.length > 3 || complaint?.videos?.length > 0)) && (
                                                            <div
                                                                className="image-overlay"
                                                                onClick={() => openLightbox(complaint, 0)}
                                                            >
                                                                <span>+{(complaint?.images?.length - 3) + (complaint?.videos?.length || 0)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {complaint?.videos?.length > 0 && complaint?.images?.length === 0 && (
                                            <div className="complaint-item-videos">
                                                {complaint?.videos?.slice(0, 3).map((video, index, array) => (
                                                    <div
                                                        key={index}
                                                        className="video-wrapper"
                                                    >
                                                        <video
                                                            src={video}
                                                            alt="Complaint"
                                                            className='complaint-video'
                                                            controls
                                                            onClick={() => openLightbox(complaint, complaint?.images?.length + index)}
                                                        />
                                                        {/* Show overlay on the last visible video only if there are more videos */}
                                                        {(index === array.length - 1 && complaint?.videos?.length > 3) && (
                                                            <div
                                                                className="video-overlay"
                                                                onClick={() => openLightbox(complaint, complaint?.images?.length)}
                                                            >
                                                                <span>+{complaint?.videos?.length - 3}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-complaints-message">
                                <p>{t('no_complaints_found')}</p>
                                <p>{t('add_complaint_message')}</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="complaint-form">
                        <div className="form-group">
                            <label htmlFor="complainDescription">{t('complaint_title')}</label>
                            <textarea
                                id="complainDescription"
                                value={complainDescription}
                                onChange={(e) => setComplainDescription(e.target.value)}
                                placeholder={t('complaint_title_placeholder')}
                                className="form-control"
                            />
                        </div>

                        <div className="form-group">
                            <label >{t('add_images_or_videos')}</label>
                            <div className="d-flex flex-col justify-between">
                                <div>
                                    {imagePreviews.length < MAX_IMAGE_COUNT && (
                                        <div className="image-upload">
                                            <input
                                                type="file"
                                                id="complaintImage"
                                                accept="image/*"
                                                multiple
                                                onChange={handleImageChange}
                                                className="file-input"
                                            />
                                            <label htmlFor="complaintImage" className="upload-label">

                                                <span className='d-flex flex-column align-items-center gap-2'>
                                                    <FaImage className="icon" />
                                                    {t('add_images')}
                                                    ({imagePreviews.length}/{MAX_IMAGE_COUNT})</span>
                                            </label>
                                        </div>
                                    )}

                                    {imagePreviews.length > 0 && (
                                        <div className="image-previews-container">
                                            {imagePreviews.map((preview, index) => (
                                                <div key={index} className="image-preview-item">
                                                    <img src={preview} alt="Preview" className="image-preview" />
                                                    <button
                                                        type="button"
                                                        className="remove-image-btn"
                                                        onClick={() => handleRemoveImage(index)}
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    {videoPreviews.length < MAX_VIDEO_COUNT && (
                                        <div className="video-upload">
                                            <input
                                                type="file"
                                                id="complaintVideo"
                                                accept="video/mp4, video/mov, video/avi, video/wmv"
                                                multiple
                                                onChange={handleVideoChange}
                                                className="file-input"
                                            />
                                            <label htmlFor="complaintVideo" className="upload-label">
                                                <span className='d-flex flex-column align-items-center gap-2'>
                                                    <FaVideo className="icon" />
                                                    {t('add_videos')}
                                                    ({videoPreviews.length}/{MAX_VIDEO_COUNT})
                                                </span>
                                            </label>
                                        </div>
                                    )}

                                    {videoPreviews.length > 0 && (
                                        <div className="video-previews-container">
                                            {videoPreviews.map((preview, index) => (
                                                <div key={index} className="video-preview-item">
                                                    <video src={preview} alt="Preview" className="video-preview" controls />
                                                    <button
                                                        type="button"
                                                        className="remove-video-btn"
                                                        onClick={() => handleRemoveVideo(index)}
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="complaint-modal-footer">
                {showComplaintForm ? (
                    <button
                        onClick={handleSubmit}
                        className='submit-btn'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="ms-2">{t('submitting')}</span>
                            </>
                        ) : t('submit')}
                    </button>)
                    : null}
                {!isUserBlocked && !showComplaintForm ?
                    (
                        <button
                            onClick={() => setShowComplaintForm(true)}
                            className='submit-btn'
                            disabled={isLoading}
                        >
                            <FaPlus /> {t('add_complaint')}
                        </button>
                    ) : null}
            </Modal.Footer>
            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                index={lightboxIndex}
                slides={currentComplaintMedia}
                plugins={[Video]}
            />
        </Modal>
    );
};

export default OrderComplaintModal;